import React, { useState } from "react";
import { Box, IconButton, Link, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { gworkspaceOAuthScopesCsv, GWORKSPACE_DOMAIN_WIDE_DELEGATION_URL } from "../../utils/constants";
import { GWORKSPACE_APIS } from "./GWorkspaceSetupGuideSteps";

/**
 * Google Workspace setup, one fragment per step of the guided run.
 *
 * The guided run does these nine things itself. When it cannot, the technician is shown the
 * fragments for exactly the steps that did not happen (Q97, Q133) -- so each fragment stands on
 * its own, in the order the run does them, and says where the values it needs come from. A
 * surface with no run to filter by shows all nine.
 */

export const GWORKSPACE_STEP_NAMES = [
  "create_admin_user",
  "assign_super_admin",
  "assign_license",
  "create_project",
  "clear_key_policies",
  "create_service_account",
  "grant_service_usage_admin",
  "authorize_delegation",
  "create_key",
  "enable_apis",
] as const;

export type GWorkspaceStepName = (typeof GWORKSPACE_STEP_NAMES)[number];

const STEP_SX = {
  fontFamily: "var(--theo-font-sans)",
  fontWeight: 600,
  fontSize: 14,
  color: "#1a1a1a",
  mb: 1,
} as const;

const LI_SX = {
  fontFamily: "var(--theo-font-sans)",
  fontSize: 13,
  color: "#555",
  lineHeight: 1.5,
} as const;

const LINK_SX = { color: "#705d4a", fontWeight: 500 } as const;

const ADMIN_USERS_URL = "https://admin.google.com/ac/users";
const CLOUD_CONSOLE_URL = "https://console.cloud.google.com/";
const ORG_POLICIES_URL = "https://console.cloud.google.com/iam-admin/orgpolicies/list";
const ORG_IAM_URL = "https://console.cloud.google.com/iam-admin/iam";
const SERVICE_ACCOUNTS_URL = "https://console.cloud.google.com/iam-admin/serviceaccounts";
const PROJECT_IAM_URL = "https://console.cloud.google.com/iam-admin/iam";

const Ext: React.FC<{ href: string; children: React.ReactNode; bold?: boolean }> = ({ href, children, bold }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ ...LINK_SX, ...(bold ? { fontWeight: 600 } : {}) }}>
    {children}
  </Link>
);

const Li: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="li" sx={LI_SX}>
    {children}
  </Typography>
);

const ScopesCopyBox: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(gworkspaceOAuthScopesCsv);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Box sx={{ mt: 1, p: 1.5, bgcolor: "#fff", border: "1px solid #e5e5e5", borderRadius: 1, display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: "#333", lineHeight: 1.5, flex: 1, wordBreak: "break-all" }}>
        {gworkspaceOAuthScopesCsv}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <IconButton
          size="small"
          onClick={copy}
          sx={{ color: copied ? "#2e7d32" : "#705d4a", p: 0.25 }}
          title={copied ? "Copied" : "Copy scopes"}
          aria-label={copied ? "OAuth scopes copied" : "Copy OAuth scopes"}
        >
          {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
        {copied ? <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11, fontWeight: 600, color: "#2e7d32", lineHeight: 1.2 }}>Copied</Typography> : null}
      </Box>
    </Box>
  );
};

/** One fragment per step: the title the checklist uses, and what to do in its place by hand. */
export const GWORKSPACE_STEP_GUIDES: Record<GWorkspaceStepName, { title: string; body: React.ReactNode }> = {
  create_admin_user: {
    title: "Create the admin user",
    body: (
      <>
        <Li>
          Sign in to the <Ext href={ADMIN_USERS_URL}>Google Admin console</Ext> with an existing Super Admin account and go to{" "}
          <strong>Directory &gt; Users</strong>.
        </Li>
        <Li>
          Click <strong>Add new user</strong>. First name <strong>TheoAI</strong>, last name <strong>Admin</strong>, and as the primary
          email the address you entered in the <strong>Super Admin Email</strong> field on this page.
        </Li>
        <Li>
          Click <strong>Add new user</strong>. Keep the generated password somewhere safe; Theo does not need it.
        </Li>
      </>
    ),
  },
  assign_super_admin: {
    title: "Assign Super Admin",
    body: (
      <>
        <Li>
          In <Ext href={ADMIN_USERS_URL}>Directory &gt; Users</Ext>, open the user from the previous step.
        </Li>
        <Li>
          Open <strong>Admin roles and privileges</strong>.
        </Li>
        <Li>
          Next to <strong>Super Admin</strong>, set the toggle to <strong>Assigned</strong> and click <strong>Save</strong>.
        </Li>
        <Li>Wait a few minutes for the role to propagate before testing.</Li>
      </>
    ),
  },
  create_project: {
    title: "Create the Cloud project",
    body: (
      <>
        <Li>
          Sign in to the <Ext href={CLOUD_CONSOLE_URL}>Google Cloud console</Ext> with the same account.
        </Li>
        <Li>
          Open the project picker at the top of the page and click <strong>New project</strong>.
        </Li>
        <Li>
          Name it <strong>TheoAI Admin</strong> (any name works), leave the organization as suggested, and click <strong>Create</strong>.
        </Li>
        <Li>
          Wait for the notification that the project is ready, then select it in the picker. Every step below is done with this project
          selected.
        </Li>
      </>
    ),
  },
  clear_key_policies: {
    title: "Clear the key-creation org policies",
    body: (
      <>
        <Li>
          The organization may enforce a policy that blocks creating service-account keys. With the project selected, go to{" "}
          <Ext href={ORG_POLICIES_URL}>IAM &amp; Admin &gt; Organization Policies</Ext> and open{" "}
          <strong>Disable service account key creation</strong> (<em>iam.disableServiceAccountKeyCreation</em>). If its status is
          already <strong>Not enforced</strong>, there is nothing to do for it.
        </Li>
        <Li>
          If <strong>Manage policy</strong> is greyed out, hover over it: the tooltip lists the missing permissions and offers{" "}
          <strong>Fix access</strong>. Click it, and in the dialog click <strong>Grant access</strong> — it grants your account{" "}
          <strong>Organization Policy Administrator</strong> on the organization. (The long way round is the organization&apos;s{" "}
          <Ext href={ORG_IAM_URL}>IAM page</Ext>.) Reload; Manage policy is now live.
        </Li>
        <Li>
          Click <strong>Manage policy</strong>. Under <strong>Policy source</strong> choose <strong>Override parent&apos;s policy</strong>{" "}
          if it is not already chosen. If a rule is listed, expand it; if the list is empty, click <strong>Add a rule</strong>. Set{" "}
          <strong>Enforcement</strong> to <strong>Off</strong> (a new rule is Off already), click <strong>Done</strong>, then{" "}
          <strong>Set policy</strong>. If a dialog asks whether to save without testing, click its <strong>Set policy</strong> too.
        </Li>
        <Li>
          Do the same for <strong>Disable service account key creation (managed)</strong> (
          <em>iam.managed.disableServiceAccountKeyCreation</em>). Both must read <strong>Not enforced</strong> before the key step.
        </Li>
      </>
    ),
  },
  create_service_account: {
    title: "Create the service account",
    body: (
      <>
        <Li>
          Go to <Ext href={SERVICE_ACCOUNTS_URL}>IAM &amp; Admin &gt; Service Accounts</Ext> and click{" "}
          <strong>Create service account</strong>.
        </Li>
        <Li>
          Service account name <strong>theoai-admin</strong>; the ID fills itself in. Click <strong>Create and continue</strong>, then{" "}
          <strong>Done</strong>. No roles are needed here.
        </Li>
        <Li>
          Open the account and note its <strong>email</strong> and its <strong>Unique ID</strong> (the client ID); both are used below.
        </Li>
      </>
    ),
  },
  grant_service_usage_admin: {
    title: "Grant the service account Service Usage Admin",
    body: (
      <>
        <Li>
          Go to <Ext href={PROJECT_IAM_URL}>IAM &amp; Admin &gt; IAM</Ext> with the project selected and click <strong>Grant access</strong>.
        </Li>
        <Li>
          Under <strong>New principals</strong>, paste the service account&apos;s email.
        </Li>
        <Li>
          Under <strong>Role</strong>, search for <strong>Service Usage Admin</strong>, select it, and click <strong>Save</strong>. This is
          what lets Theo enable APIs on the project.
        </Li>
      </>
    ),
  },
  create_key: {
    title: "Create the JSON key",
    body: (
      <>
        <Li>
          Go to <Ext href={SERVICE_ACCOUNTS_URL}>IAM &amp; Admin &gt; Service Accounts</Ext>, open the account, and click the{" "}
          <strong>Keys</strong> tab.
        </Li>
        <Li>
          Click <strong>Add key &gt; Create new key</strong>, choose <strong>JSON</strong>, and click <strong>Create</strong>. The browser
          downloads the key file.
        </Li>
        <Li>
          Upload that file in the <strong>Service Account JSON</strong> field on this page.
        </Li>
      </>
    ),
  },
  enable_apis: {
    title: "Enable the APIs",
    body: (
      <>
        <Li>
          With the project selected, open each of the following and click <strong>Enable</strong>:
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, "& li": { mb: 0.5 } }}>
            {GWORKSPACE_APIS.map((api) => (
              <Li key={api.label}>
                <Ext href={api.url}>{api.label}</Ext>
              </Li>
            ))}
          </Box>
        </Li>
      </>
    ),
  },
  authorize_delegation: {
    title: "Authorize domain-wide delegation",
    body: (
      <>
        <Li>
          Copy the service account&apos;s <strong>Unique ID</strong> (the client ID) from{" "}
          <Ext href={SERVICE_ACCOUNTS_URL}>IAM &amp; Admin &gt; Service Accounts</Ext>.
        </Li>
        <Li>
          In the Admin console, go to{" "}
          <Ext href={GWORKSPACE_DOMAIN_WIDE_DELEGATION_URL}>
            Security &gt; Access and data control &gt; API controls &gt; Manage Domain Wide Delegation
          </Ext>
          .
        </Li>
        <Li>
          Click <strong>Add new</strong>, paste the client ID, and paste the following into <strong>OAuth scopes</strong>:
          <ScopesCopyBox />
        </Li>
        <Li>
          Click <strong>Authorize</strong>.
        </Li>
      </>
    ),
  },
  assign_license: {
    title: "Assign a Google Workspace license",
    body: (
      <>
        <Li>
          In <Ext href={ADMIN_USERS_URL}>Directory &gt; Users</Ext>, open the admin user and click <strong>Licenses</strong>.
        </Li>
        <Li>
          Click the <strong>Edit</strong> pencil on the Licenses card, turn on a paid <strong>Google Workspace</strong> edition (Business
          Starter, Standard, Plus or Enterprise -- not Cloud Identity Free) and click <strong>Save</strong>.
        </Li>
        <Li>
          Reload the page and confirm the edition reads <strong>Assigned</strong>. Without a license, Gmail, Calendar, Chat and Drive actions
          will not work for this account; the connection test reports it as a warning.
        </Li>
      </>
    ),
  },
};

export interface GWorkspaceManualStepInstructionsProps {
  /** Which steps to show, in run order. Omit for all nine. */
  steps?: readonly string[];
  heading?: string | null;
}

export const GWorkspaceManualStepInstructions: React.FC<GWorkspaceManualStepInstructionsProps> = ({ steps, heading = "Setup Guide" }) => {
  const wanted = new Set(steps ?? GWORKSPACE_STEP_NAMES);
  const shown = GWORKSPACE_STEP_NAMES.filter((name) => wanted.has(name));
  if (shown.length === 0) return null;
  return (
    <Box data-testid="gworkspace-manual-steps">
      {heading ? (
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 15, color: "#1a1a1a", mb: 1.5 }}>{heading}</Typography>
      ) : null}
      {shown.map((name) => {
        const guide = GWORKSPACE_STEP_GUIDES[name];
        const number = GWORKSPACE_STEP_NAMES.indexOf(name) + 1;
        return (
          <Box key={name} data-testid={`gworkspace-manual-step-${name}`}>
            <Typography sx={STEP_SX}>
              Step {number}: {guide.title}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, "& li": { mb: 0.75 } }}>
              {guide.body}
            </Box>
          </Box>
        );
      })}
      <Box sx={{ p: 1.5, bgcolor: "#fff", border: "1px solid #e5e5e5", borderRadius: 1, mb: 1 }}>
        <Typography sx={{ ...LI_SX, color: "#1a1a1a" }}>
          When these are done, click <strong>Test</strong>. <strong>Save</strong> becomes available once the test passes.
        </Typography>
      </Box>
    </Box>
  );
};

export default GWorkspaceManualStepInstructions;
